import {
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { DialogBox } from '@duke-hq/ui/components/DialogBox/DialogBox';
import { AssessmentOutcomeSchema } from '@duke-hq/svc-api-db/schemas/enums/AssessmentOutcome.schema';
import { AssessmentOutcome } from '@duke-hq/svc-api-db';
import { SemesterCompletionAlert } from '../../components/SemesterCompletionAlert';

interface AssessmentOutcomeConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pendingOutcome: AssessmentOutcome | null;
  isCourseEnrolmentCompleted: boolean;
  willCompleteCourseWhenSatisfactory: boolean;
  isLoading?: boolean;
}

export function AssessmentOutcomeConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  pendingOutcome,
  isCourseEnrolmentCompleted,
  willCompleteCourseWhenSatisfactory,
  isLoading = false,
}: AssessmentOutcomeConfirmationDialogProps) {
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleClose = () => {
    setIsConfirmed(false);
    onClose();
  };

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size="lg"
      contentProps={{
        maxW: '600px',
        minW: '500px',
      }}
      header={<Trans>Update assessment outcome</Trans>}
      body={
        <VStack align="start" spacing={4} py={2}>
          <Box fontSize="md" lineHeight="tall">
            {pendingOutcome === AssessmentOutcomeSchema.enum.SATISFACTORY ? (
              isCourseEnrolmentCompleted ? (
                // SATISFACTORY => SATISFACTORY
                <Alert status="warning" borderRadius="md" p={4} alignItems="flex-start">
                  <AlertIcon />
                  <AlertDescription>
                    <Text>
                      <Trans>
                        This enrolment has been previously marked as <strong>Satisfactory</strong>{' '}
                        for this semester.
                      </Trans>
                    </Text>
                    <Text mt={4}>
                      <Trans>
                        Updating the outcome of this assessment will revoke all documentation until
                        this unit is marked as satisfactory.
                      </Trans>
                    </Text>
                  </AlertDescription>
                </Alert>
              ) : willCompleteCourseWhenSatisfactory ? (
                // NOT_YET_SATISFACTORY => SATISFACTORY or PENDING => SATISFACTORY
                <SemesterCompletionAlert />
              ) : null
            ) : isCourseEnrolmentCompleted ? (
              // SATISFACTORY => NOT_YET_SATISFACTORY or SATISFACTORY => PENDING
              <Alert status="warning" borderRadius="md" p={4} alignItems="flex-start">
                <AlertIcon />
                <AlertDescription>
                  <Text>
                    <Trans>
                      This enrolment has been previously marked as <strong>Satisfactory</strong> for
                      this semester.
                    </Trans>
                  </Text>
                  <Text mt={4}>
                    <Trans>
                      Updating the outcome of this assessment will revoke all documentation until
                      this unit is marked as satisfactory.
                    </Trans>
                  </Text>
                </AlertDescription>
              </Alert>
            ) : null}
          </Box>
          <Checkbox
            isChecked={isConfirmed}
            onChange={(e) => setIsConfirmed(e.target.checked)}
            fontSize="sm"
          >
            {willCompleteCourseWhenSatisfactory
              ? t`I understand this will result in the completion of this semester.`
              : t`I understand changing this outcome will revoke documentation for this enrolment.`}
          </Checkbox>
        </VStack>
      }
      footer={
        <ButtonGroup>
          <Button variant="tertiary" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={onConfirm} isLoading={isLoading} isDisabled={!isConfirmed}>
            <Trans>Update outcome</Trans>
          </Button>
        </ButtonGroup>
      }
    />
  );
}
